// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useState } from 'react';
import { Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';
import HolePointsGrid, { resolveHoleGridColumns } from '@/features/tour-games/components/HolePointsGrid';
import type { HoleGridCell } from '@/features/tour-games/components/HolePointsGrid';
import { buildFreezeRows, findOpenWindowStandings, getLockableHoles, rankByLockedPoints } from '@/features/tour-games/freeze';
import type { FreezeSection } from '@/features/tour-games/types';
import { styles } from '../styles';

type Props = {
  section: FreezeSection | null;
  playerNameLookup: Map<string, string>;
  // Ladder merit payout per player (from the tour game results projection);
  // present once the last round is underway or the tour is finished.
  meritsByPlayerId?: Map<string, number>;
  onOpenLockScreen?: (sourceRoundId: string) => void;
  // Round-summary usage keeps the CTA visible-but-disabled once no player has
  // an open lock window, instead of the leaderboard's default of hiding it.
  showDisabledCtaWhenClosed?: boolean;
  // Round-summary usage shows every player's hole grid open by default (still
  // individually collapsible), instead of the leaderboard's one-at-a-time accordion.
  expandAllByDefault?: boolean;
};

// Freeze standings: mid-tour only locked (frozen) points are shown and ranked;
// open holes join the totals once the tour result is final. Anyone in the
// round can lock holes for any player while a window is open.
export default function FreezeStandingsCard({
  section,
  playerNameLookup,
  meritsByPlayerId,
  onOpenLockScreen,
  showDisabledCtaWhenClosed,
  expandAllByDefault,
}: Props) {
  const { t } = useI18n();
  const { width: viewportWidth } = useWindowDimensions();
  const [expandedPlayerId, setExpandedPlayerId] = useState<string | null>(null);
  // In expand-all mode every row starts open; this tracks the ones a player
  // has individually collapsed, rather than the ones that are open.
  const [collapsedPlayerIds, setCollapsedPlayerIds] = useState<Set<string>>(new Set());
  // Screen (14px) and card (12px) padding on each side.
  const gridColumns = resolveHoleGridColumns(viewportWidth - 52);

  if (!section) return null;
  // Once the last round is underway the provisional final result is shown:
  // every hole gets points and rows rank by the live total.
  const showFinalResult = section.isFinal || section.isFinalRound;
  const allRows = buildFreezeRows(section, playerNameLookup, t('tourLeaderboard.unknownPlayer'));
  const rows = showFinalResult ? allRows : rankByLockedPoints(allRows);
  if (rows.length === 0) return null;

  const openWindows = findOpenWindowStandings(section);
  const lockRoundId = openWindows.find((standing) => standing.window.sourceRoundId)?.window.sourceRoundId ?? null;
  // A window can stay "open" (per round progression) after every hole it could
  // lock is already locked, so the CTA also needs an actual lockable hole
  // remaining — otherwise it stays enabled with nothing left to freeze.
  const anyLockableRemaining = section.standings.some(
    (standing) => getLockableHoles(standing.holes).length > 0
  );
  const ctaEnabled = !section.isFinal && !!lockRoundId && anyLockableRemaining && !!onOpenLockScreen;
  const showCta = ctaEnabled || (showDisabledCtaWhenClosed && !section.isFinal && !!onOpenLockScreen);

  return (
    <View style={styles.summarySectionCard}>
      <Text style={styles.summarySectionTitle}>{t('tourLeaderboard.freezeTitle')}</Text>
      <Text style={localStyles.subtitle}>
        {section.isFinal
          ? t('tourLeaderboard.freezeFinalSubtitle')
          : section.isFinalRound
            ? t('tourLeaderboard.freezeFinalRoundSubtitle')
            : t('tourLeaderboard.freezeSubtitle')}
      </Text>
      {rows.map((row) => {
        const expanded = expandAllByDefault
          ? !collapsedPlayerIds.has(row.playerId)
          : expandedPlayerId === row.playerId;
        const toggleRow = () => {
          if (expandAllByDefault) {
            setCollapsedPlayerIds((prev) => {
              const next = new Set(prev);
              if (next.has(row.playerId)) {
                next.delete(row.playerId);
              } else {
                next.add(row.playerId);
              }
              return next;
            });
            return;
          }
          setExpandedPlayerId(expanded ? null : row.playerId);
        };
        const cells: HoleGridCell[] = row.holes.map((hole) => ({
          holeNumber: hole.holeNumber,
          // Mid-tour only frozen holes show points; open holes stay blank
          // until the last round is underway.
          points: showFinalResult || hole.locked ? hole.points : null,
          sublabel: hole.stale ? t('tourLeaderboard.freezeStale') : undefined,
          state: hole.locked ? 'locked' : 'plain',
        }));
        return (
          <View key={row.playerId}>
            <Pressable
              style={styles.summaryPlayerRow}
              onPress={toggleRow}
              accessibilityRole="button"
              testID={`freeze-row-${row.playerId}`}
            >
              <Text style={styles.summaryRank}>{`${row.position}.`}</Text>
              <View style={styles.summaryNameBlock}>
                <Text style={styles.summaryPlayerName} numberOfLines={1}>{row.name}</Text>
                <Text style={styles.summaryPlayerMeta}>
                  {t('tourLeaderboard.freezeLockedCount', {
                    locked: row.lockedHoleCount,
                    total: section.holeCount,
                  })}
                  {row.lockedHoleCount > 0
                    ? ` · ${t('tourLeaderboard.freezeLockedAvg', {
                        avg: (row.lockedPoints / row.lockedHoleCount).toFixed(1),
                      })}`
                    : ''}
                </Text>
              </View>
              <View style={localStyles.pointsBlock}>
                <Text style={styles.summaryMerits} testID={`freeze-points-${row.playerId}`}>
                  {t('tourLeaderboard.gamePoints', {
                    points: showFinalResult ? row.totalPoints : row.lockedPoints,
                  })}
                </Text>
                {meritsByPlayerId?.has(row.playerId) ? (
                  <Text style={localStyles.meritsText} testID={`freeze-merits-${row.playerId}`}>
                    {t('tourLeaderboard.freezeMerits', { merits: meritsByPlayerId.get(row.playerId) ?? 0 })}
                  </Text>
                ) : null}
              </View>
            </Pressable>
            {expanded ? (
              <View style={localStyles.gridWrap}>
                <HolePointsGrid cells={cells} testIDPrefix={`freeze-${row.playerId}`} columns={gridColumns} />
                <Text style={localStyles.gridHint}>
                  {showFinalResult
                    ? t('tourLeaderboard.freezeGridHint', { locked: row.lockedPoints, open: row.unlockedPoints })
                    : t('tourLeaderboard.freezeGridHintLocked', { locked: row.lockedPoints })}
                </Text>
              </View>
            ) : null}
          </View>
        );
      })}
      {showCta ? (
        <View style={localStyles.ctaWrap}>
          <GoffyButton
            label={t('tourLeaderboard.freezeLockCta')}
            onPress={() => ctaEnabled && lockRoundId && onOpenLockScreen!(lockRoundId)}
            disabled={!ctaEnabled}
            testID="freeze-open-lock-screen"
          />
        </View>
      ) : null}
    </View>
  );
}

const localStyles = StyleSheet.create({
  subtitle: {
    fontSize: 12,
    color: appColors.text.muted,
    marginBottom: 8,
  },
  pointsBlock: {
    alignItems: 'flex-end',
    backgroundColor: 'transparent',
  },
  meritsText: {
    fontSize: 11,
    color: appColors.text.muted,
    marginTop: 2,
  },
  gridWrap: {
    paddingVertical: 8,
    backgroundColor: 'transparent',
  },
  gridHint: {
    fontSize: 11,
    color: appColors.text.muted,
    marginTop: 6,
  },
  ctaWrap: {
    marginTop: 10,
    backgroundColor: 'transparent',
  },
});
